import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Gauge } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

/**
 * FT-4 — the unit rate per scope, shown on the scope in RA. minutes per unit = budgeted hours ×
 * factor ÷ countable units in the Current FRS. Override by hand (logged); the company default
 * from actuals learning sits beside it and never replaces a budgeted rate.
 */
export default function ScopeRatesPanel({ projectId }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try { setData((await api.get(`/scheduling/projects/${projectId}/rates`)).data); } catch { setData({ scopes: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  if (!data) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="scope-rates-panel">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <Gauge className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Minutes per countable unit</span>
        <span className="text-xs text-gray-400">budgeted hours × {data.scopes[0]?.duration_budget_factor ?? 0.8} ÷ countable units in the Current FRS</span>
        <span className={`ml-auto text-[10px] uppercase font-semibold rounded px-1.5 py-0.5 border ${
          data.auto_durations ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
              data-testid="scope-rates-auto-badge">
          auto durations {data.auto_durations ? "on" : "off"}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-[10px] text-gray-500 uppercase tracking-wider">
            <th className="text-left px-4 py-2 font-medium">Scope</th>
            <th className="text-right px-3 py-2 font-medium">Countable units</th>
            <th className="text-right px-3 py-2 font-medium">Excluded</th>
            <th className="text-right px-3 py-2 font-medium">Budget rate</th>
            <th className="text-right px-3 py-2 font-medium">Actual / drift</th>
            <th className="text-left px-3 py-2 font-medium">Override (min/unit)</th>
            <th className="text-right px-3 py-2 font-medium">Company default</th>
            <th className="text-right px-3 py-2 font-medium">In use</th>
          </tr>
        </thead>
        <tbody>
          {data.scopes.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">No FRS rows to count yet.</td></tr>
          )}
          {data.scopes.map((s) => <RateRow key={s.scope_type} s={s} projectId={projectId} onSaved={load} />)}
        </tbody>
      </table>
    </div>
  );
}

function RateRow({ s, projectId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [val, setVal] = useState(s.minutes_per_unit_override ?? "");
  const [reason, setReason] = useState("");
  useEffect(() => { setVal(s.minutes_per_unit_override ?? ""); }, [s.minutes_per_unit_override]);
  const st = s.scope_type;
  const uoms = Object.entries(s.units_by_uom || {}).map(([u, n]) => `${n} ${u}`).join(" · ");

  const save = async (clear = false) => {
    if (!reason.trim()) { toast.error("Say why the rate is set by hand"); return; }
    try {
      await api.patch(`/scheduling/projects/${projectId}/rates/${encodeURIComponent(st)}`,
        clear ? { clear_override: true, reason } : { minutes_per_unit: Number(val), reason });
      toast.success(clear ? "Back to the budgeted rate" : "Rate override saved (logged)");
      setReason("");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const togglePreview = async () => {
    if (!open && !preview) {
      try { setPreview((await api.get(`/scheduling/projects/${projectId}/countable-preview`, { params: { scope_type: st } })).data); } catch { setPreview({ products: [] }); }
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <tr className="border-t border-gray-100" data-testid={`rate-row-${st}`}>
        <td className="px-4 py-2 font-medium text-gray-800">
          <button type="button" onClick={togglePreview} className="inline-flex items-center gap-1" data-testid={`rate-products-toggle-${st}`}>
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {s.scope_code || st}
          </button>
          {s.scope_code && s.scope_code !== st && <span className="text-xs text-gray-400 ml-1.5">{st}</span>}
          {s.countable_override && <span className="ml-2 text-[10px] uppercase text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1">job definition</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums" data-testid={`rate-units-${st}`}>
          {s.countable_units}
          {uoms && <div className="text-[10px] text-gray-400">{uoms}</div>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{s.excluded_units || "—"}</td>
        <td className="px-3 py-2 text-right tabular-nums" data-testid={`rate-budget-${st}`}>
          {s.minutes_per_unit_budget != null ? `${s.minutes_per_unit_budget} min` : <span className="text-gray-400">no budget</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums" data-testid={`rate-drift-${st}`}
            title={s.drift_message || ""}>
          {s.actual_minutes_per_unit != null ? (
            <>
              {s.actual_minutes_per_unit} min
              {s.drift != null && (
                <div className={`text-[10px] font-semibold ${
                  s.drift_level === "over" ? "text-rose-700"
                  : s.drift_level === "warn" ? "text-amber-700" : "text-gray-400"}`}>
                  {s.drift >= 0 ? "+" : ""}{Math.round(s.drift * 100)}%
                  {s.drift_level === "ok" && !s.drift_ready ? " · early" : ""}
                </div>
              )}
            </>
          ) : <span className="text-gray-400">—</span>}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1">
            <Input type="number" min={0} step="0.5" value={val} onChange={(e) => setVal(e.target.value)} placeholder="—"
                   className="h-8 w-20" data-testid={`rate-override-${st}`} />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why?" className="h-8 w-32 text-xs" data-testid={`rate-reason-${st}`} />
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={val === "" || Number(val) <= 0} onClick={() => save(false)} data-testid={`rate-save-${st}`}>Set</Button>
            {s.minutes_per_unit_override != null && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => save(true)} data-testid={`rate-clear-${st}`}>Clear</Button>
            )}
          </div>
          {s.override_log?.length > 0 && (
            <div className="text-[10px] text-gray-400 mt-0.5" data-testid={`rate-log-${st}`}>
              last: {s.override_log[s.override_log.length - 1].by} — {s.override_log[s.override_log.length - 1].reason}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-500" data-testid={`rate-company-${st}`}>
          {s.company_default_minutes_per_unit != null ? `${s.company_default_minutes_per_unit} min` : "—"}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-semibold" data-testid={`rate-effective-${st}`}>
          {s.minutes_per_unit != null ? `${s.minutes_per_unit} min` : "—"}
          <div className="text-[10px] font-normal text-gray-400">{s.rate_source.replace("_", " ")}{s.auto_hours != null ? ` · ${s.auto_hours} h` : ""}</div>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60" data-testid={`rate-products-${st}`}>
          <td colSpan={8} className="px-6 py-2">
            <div className="text-[11px] text-gray-500 mb-1">Which products count — from the {s.countable_override ? "project" : "company"} definition{!s.definition && " (none: every product counts as each)"}</div>
            <div className="flex flex-wrap gap-1.5">
              {(preview?.products || []).map((p) => (
                <span key={p.product_uid || p.product_code} title={p.why}
                      className={`text-[11px] rounded-full px-2 py-0.5 border ${p.countable ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-gray-100 border-gray-200 text-gray-400 line-through"}`}>
                  {p.product_short || p.product_code || p.product_description} · {p.quantity}{p.countable ? ` ${p.uom}` : ""}
                </span>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
